import { useEffect, useState } from 'react';
import type {
  ExpensesByCategory,
  MonthlyPnlRow,
  PnlSummary,
  PublicUser,
  YtdKpis,
} from '@otter/shared';
import { api } from '../lib/api';
import { fmt, fmtDate } from '../lib/format';

type Props = { user: PublicUser; scope: string; onError: (e: unknown) => void };

type TaxObligation = {
  id: string;
  modelCode: string;
  description: string;
  periodLabel: string;
  dueDate: string;
  status: 'presentado' | 'proximo' | 'no_aplica';
  estimatedAmount: string | null;
};

const CAT_COLORS = [
  'var(--accent)',
  '#e89a3e',
  '#6e8aff',
  '#c8693e',
  '#b16fe8',
  '#2f7a4a',
  '#a8351a',
];

function Donut({ data, size = 200, stroke = 32 }: {
  data: Array<{ label: string; value: number; color: string }>;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const total = data.reduce((a, d) => a + d.value, 0) || 1;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--line2)" strokeWidth={stroke} />
      {data.map((d, i) => {
        const len = (d.value / total) * circ;
        const dashArray = `${len} ${circ}`;
        const el = (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={d.color}
            strokeWidth={stroke}
            strokeDasharray={dashArray}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
        offset += len;
        return el;
      })}
    </svg>
  );
}

export function Reportes({ scope, onError }: Props) {
  const [ytd, setYtd] = useState<YtdKpis | null>(null);
  const [monthly, setMonthly] = useState<MonthlyPnlRow[]>([]);
  const [expensesByCat, setExpensesByCat] = useState<ExpensesByCategory[]>([]);
  const [pnl, setPnl] = useState<PnlSummary | null>(null);
  const [taxes, setTaxes] = useState<TaxObligation[]>([]);

  useEffect(() => {
    const q = scope !== 'all' ? { scope } : undefined;
    void (async () => {
      try {
        const [y, m, e, p, t] = await Promise.all([
          api<YtdKpis>('/admin/reports/ytd', { query: q }),
          api<{ items: MonthlyPnlRow[] }>('/admin/reports/monthly', { query: q }),
          api<{ items: ExpensesByCategory[] }>('/admin/reports/expenses-by-category', { query: q }),
          api<PnlSummary>('/admin/reports/pnl-summary', { query: q }),
          api<{ items: TaxObligation[] }>('/admin/tax-obligations'),
        ]);
        setYtd(y);
        setMonthly(m.items);
        setExpensesByCat(e.items);
        setPnl(p);
        setTaxes(t.items);
      } catch (err) {
        onError(err);
      }
    })();
  }, [scope, onError]);

  const totalExpenses = expensesByCat.reduce((a, e) => a + e.total, 0);
  const donutData = expensesByCat.map((e, i) => ({
    label: e.category,
    value: e.total,
    color: CAT_COLORS[i % CAT_COLORS.length] ?? 'var(--accent)',
  }));
  const filledMonths = monthly.filter((m) => m.income > 0 || m.expenses > 0 || m.draft > 0);
  const maxBar = Math.max(
    1,
    ...filledMonths.flatMap((m) => [m.income, m.expenses]),
  );
  const currentMonthIdx = new Date().getMonth();

  return (
    <div className="body">
      <div className="pageh">
        <h1>
          Reportes <span className="v2-pill">P&amp;L</span>
        </h1>
        <div className="sub">ejercicio {new Date().getFullYear()} · sin IVA</div>
      </div>

      {ytd && (
        <div className="pl-summary">
          <PlCard label="Ingresos YTD" value={fmt(ytd.income)} delta={ytd.incomeDeltaPct} />
          <PlCard label="Gastos YTD" value={fmt(ytd.expenses)} delta={ytd.expensesDeltaPct} invertDelta />
          <PlCard
            label="Margen YTD"
            value={fmt(ytd.margin)}
            valueColor="var(--accent-text)"
            delta={ytd.marginDeltaPct}
          />
          <PlCard
            label="Margen %"
            value={`${ytd.marginPct.toFixed(1)}%`}
            deltaLabel="objetivo · 80%"
          />
        </div>
      )}

      <div style={{ height: 12 }} />

      <section className="card">
        <div className="head">
          <h3>Ingresos vs. gastos · mes a mes</h3>
          <div className="meta">€ · sin IVA</div>
        </div>
        <div className="pl-grid">
          {monthly.map((m, i) => {
            const isCurr = i === currentMonthIdx;
            return (
              <div className={`col ${isCurr ? 'curr' : ''}`} key={m.month}>
                <div className="bars">
                  <div
                    className="bar ing"
                    style={{ height: `${(m.income / maxBar) * 100}%` }}
                    data-v={`Ingresos · ${fmt(m.income)}`}
                  />
                  <div
                    className="bar gas"
                    style={{ height: `${(m.expenses / maxBar) * 100}%` }}
                    data-v={`Gastos · ${fmt(m.expenses)}`}
                  />
                </div>
                <div className="lab">{m.monthLabel}</div>
              </div>
            );
          })}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 16,
            padding: '0 16px 14px',
            fontFamily: 'Geist Mono, monospace',
            fontSize: 10.5,
            color: 'var(--muted)',
          }}
        >
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                background: 'var(--accent)',
                borderRadius: 2,
                marginRight: 6,
              }}
            />
            Ingresos
          </span>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                background: 'var(--ink2)',
                opacity: 0.55,
                borderRadius: 2,
                marginRight: 6,
              }}
            />
            Gastos
          </span>
        </div>
      </section>

      <div style={{ height: 12 }} />

      <div className="row2">
        <section className="card">
          <div className="head">
            <h3>Gastos por categoría</h3>
            <div className="meta">{fmt(totalExpenses)} · YTD</div>
          </div>
          <div className="pad">
            {donutData.length > 0 ? (
              <div className="donut-wrap">
                <div style={{ position: 'relative' }}>
                  <Donut data={donutData} size={200} stroke={32} />
                  <div className="center-label">
                    <div className="v">{Math.round(totalExpenses).toLocaleString('es-ES')}€</div>
                    <div className="lab">gasto YTD</div>
                  </div>
                </div>
                <div className="donut-legend">
                  {donutData.map((d) => {
                    const pct = totalExpenses > 0 ? (d.value / totalExpenses) * 100 : 0;
                    return (
                      <div className="row" key={d.label}>
                        <span className="sw" style={{ background: d.color }} />
                        <span>{d.label}</span>
                        <span className="v">{fmt(d.value)}</span>
                        <span className="pct">{pct.toFixed(1)}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty">sin gastos registrados</div>
            )}
          </div>
        </section>

        {pnl && (
          <section className="card">
            <div className="head">
              <h3>Cuenta de resultados · resumen</h3>
              <div className="meta">YTD · ejercicio {new Date().getFullYear()}</div>
            </div>
            <table className="t">
              <tbody>
                <tr>
                  <td>Ingresos por servicios</td>
                  <td className="num">
                    <b>{fmt(pnl.incomeTotal)}</b>
                  </td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--muted)', paddingLeft: 24, fontSize: 11.5 }}>— sujetos a IRPF</td>
                  <td className="num" style={{ color: 'var(--muted)' }}>{fmt(pnl.incomeIrpfSubject)}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--muted)', paddingLeft: 24, fontSize: 11.5 }}>— resto</td>
                  <td className="num" style={{ color: 'var(--muted)' }}>{fmt(pnl.incomeRest)}</td>
                </tr>
                <tr>
                  <td>Gastos deducibles</td>
                  <td className="num" style={{ color: 'var(--red-text)' }}>−{fmt(pnl.expensesTotal)}</td>
                </tr>
                {pnl.expensesByCategory.slice(0, 4).map((g) => (
                  <tr key={g.category}>
                    <td style={{ color: 'var(--muted)', paddingLeft: 24, fontSize: 11.5 }}>— {g.category.toLowerCase()}</td>
                    <td className="num" style={{ color: 'var(--muted)' }}>{fmt(g.total)}</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--hover)' }}>
                  <td>
                    <b>Base imponible IRPF</b>
                  </td>
                  <td className="num">
                    <b style={{ color: 'var(--accent-text)', fontSize: 14 }}>{fmt(pnl.taxBase)}</b>
                  </td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--muted)' }}>IRPF retenido en facturas</td>
                  <td className="num" style={{ color: 'var(--muted)' }}>−{fmt(pnl.irpfWithheld)}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--muted)' }}>Pagos a cuenta IRPF (M130)</td>
                  <td className="num" style={{ color: 'var(--muted)' }}>{fmt(pnl.irpfPaymentsToAccount)}</td>
                </tr>
                <tr>
                  <td>
                    <b>Estimación a pagar</b>
                  </td>
                  <td className="num">
                    <b style={{ color: 'var(--red-text)', fontSize: 14 }}>{fmt(pnl.estimatedToPay)}</b>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        )}
      </div>

      <div style={{ height: 12 }} />

      <section className="card">
        <div className="head">
          <h3>Calendario fiscal</h3>
          <div className="meta">obligaciones del ejercicio</div>
        </div>
        <table className="t">
          <thead>
            <tr>
              <th>Modelo</th>
              <th>Descripción</th>
              <th>Periodo</th>
              <th>Plazo</th>
              <th>Estado</th>
              <th className="num">Estimado</th>
            </tr>
          </thead>
          <tbody>
            {taxes.map((t) => (
              <tr key={t.id}>
                <td className="mono" data-label="Modelo">
                  <b>{t.modelCode}</b>
                </td>
                <td data-label="Descripción">{t.description}</td>
                <td className="mono" style={{ color: 'var(--muted)' }} data-label="Periodo">{t.periodLabel}</td>
                <td className="mono" style={{ color: 'var(--muted)' }} data-label="Plazo">{fmtDate(t.dueDate)}</td>
                <td data-label="Estado">
                  <span
                    className={`pill ${
                      t.status === 'presentado' ? 'ok' : t.status === 'no_aplica' ? 'draft' : 'draft'
                    }`}
                  >
                    {t.status === 'no_aplica' ? 'no aplica' : t.status}
                  </span>
                </td>
                <td className="num" data-label="Estimado">{t.estimatedAmount != null ? fmt(t.estimatedAmount) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {taxes.length === 0 && (
          <div className="empty">
            No hay obligaciones fiscales registradas — créalas desde Admin → Fiscal
          </div>
        )}
      </section>
    </div>
  );
}

function PlCard({
  label,
  value,
  delta,
  deltaLabel,
  invertDelta,
  valueColor,
}: {
  label: string;
  value: string;
  delta?: number | null;
  deltaLabel?: string;
  invertDelta?: boolean;
  valueColor?: string;
}) {
  let deltaClass = 'flat';
  let deltaText = deltaLabel ?? '—';
  if (delta != null) {
    const isUp = delta > 0;
    deltaClass = (isUp ? 'up' : delta < 0 ? 'down' : 'flat');
    // For "gastos" higher is bad → invert the up/down semantics visually.
    if (invertDelta) deltaClass = isUp ? 'down' : delta < 0 ? 'up' : 'flat';
    deltaText = `${isUp ? '↑' : delta < 0 ? '↓' : '·'} ${Math.abs(delta).toFixed(1)}% vs. año anterior`;
  }
  return (
    <div className="pl-card">
      <div className="lab">{label}</div>
      <div className="v" style={valueColor ? { color: valueColor } : undefined}>
        {value}
      </div>
      <div className={`delta ${deltaClass}`}>{deltaText}</div>
    </div>
  );
}
